import React, { useMemo, useState } from 'react'
import { Dialog } from '@headlessui/react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Plus, User, MoreVertical, X, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { logError } from '../../utils/logger'
import { RootState, AppDispatch } from '../../app/store'
import { 
  openModal,
  closeModal,
  clearCustomerData,
  resetOrder
} from '../../features/customer/customerSlice'
import { clearCartItemsImmediately, clearCartItemsFromApi, selectCartInfo } from '../../features/cart/cartItemsSlice'
import { selectCartId } from '../../features/cart/cartItemsSlice'
import { clearDiscount } from '../../features/discount/discountSlice'
import { clearSelectedTable } from '../../features/tables/tablesSlice'
import { useRemoveCouponMutation, useRemoveTableMutation, useUpdateCartUserMutation, useLazyGetCartDataQuery } from '../../features/cart/cartAPI'
import { loadCartItems, updateCartDataFromApi } from '../../features/cart/cartItemsSlice'
import apiService from '../../services/api'
import { buildEndpoint } from '../../utils/apiUtils'
import { resetCsFlow, selectSelectedTypeId } from '../../features/csFlow/csFlowSlice'
import {
  refreshCsCartItems,
  selectCsCartId,
  clearCartLocally as clearCsCartLocally,
  resetCart as resetCsCart,
} from '../../features/csFlow/csCartSlice'
import { csFlowApi } from '../../features/csFlow/csFlowAPI'
import CustomerModal from '../customer/CustomerModal'
// SVG icons are now referenced directly from public folder for production compatibility
const refreshIcon = '/assets/images/refresh.svg'
const salleIcon = '/assets/images/salle.svg'
const deleteUserIcon = '/assets/images/delete_user.svg'
import usersIcon from '../../assets/images/users.svg'
import carOrdersIcon from '../../assets/images/carOrders.svg'

const getOrderTypeLabel = (orderType: string | null | undefined, t: (key: string) => string): string => {
  if (!orderType) {
    return ''
  }
  const normalized = orderType.trim().toLowerCase()

  if (normalized.includes('car pickup') || normalized.includes('استلام من السيارة') || normalized.includes('في السيارة')) {
    return t('cs.order_type_car_pickup') || 'في السيارة'
  }

  if (normalized.includes('delivery') || normalized.includes('توصيل')) {
    return t('cs.order_type_delivery') || 'توصيل'
  }

  if (
    normalized.includes('take away') ||
    normalized.includes('takeaway') ||
    normalized.includes('سفري')
  ) {
    return t('cs.order_type_takeaway') || 'استلام'
  }

  if (normalized.includes('dine') || normalized.includes('محلي')) {
    return t('cs.order_type_dine_in') || 'في الفرع'
  }

  return orderType
}

interface CustomerInfoHeaderProps {
  isCustomerService?: boolean
  isCsFlowComplete?: boolean
  onSelectOrderDetails?: () => void
}

const CustomerInfoHeader: React.FC<CustomerInfoHeaderProps> = ({
  isCustomerService = false,
  isCsFlowComplete = false,
  onSelectOrderDetails
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false)
  const [isClearingCart, setIsClearingCart] = useState(false)
  
  const { 
    hasCustomerData,
    customerName,
    customerPhone,
    customerId,
    isModalOpen
  } = useSelector((state: RootState) => state.customer)

  // Get CS Flow state for customer number validation
  const isCustomerNumberValid = useSelector((state: RootState) => state.csFlow.isCustomerNumberValid)

  const {
    selectedOrderType,
    carColor,
    carBrand,
    carModel,
    selectedBranchId,
    hasCompletedOrderDetails,
  } = useSelector((state: RootState) => state.csFlow)

  const { items } = useSelector((state: RootState) => state.cart)
  const cartId = useSelector(selectCartId)
  const cartInfo = useSelector(selectCartInfo)
  const { hasDiscount } = useSelector((state: RootState) => state.discount)
  const { selectedTable } = useSelector((state: RootState) => state.tables)
  const csCustomerId = useSelector((state: RootState) => state.csFlow.customerId)
  const csCartId = useSelector(selectCsCartId)
  const selectedTypeId = useSelector(selectSelectedTypeId)
  const [removeCoupon] = useRemoveCouponMutation()
  const [removeTable] = useRemoveTableMutation()
  const [updateCartUser] = useUpdateCartUserMutation()
  const [getCartData] = useLazyGetCartDataQuery()

  const isRTL = useSelector((state: RootState) => state.settings.settings.language === 'ar')

  const branchLabel = useMemo(() => {
    if (typeof window === 'undefined') {
      return ''
    }
    try {
      const storedValue = localStorage.getItem('selected_branch_data')
      if (!storedValue) {
        return ''
      }
      const parsedBranch = JSON.parse(storedValue)
      const branchName = parsedBranch?.display_name || parsedBranch?.name || ''
      if (typeof branchName === 'string' && branchName.trim()) {
        return branchName.trim()
      }
    } catch (error) {
      // Ignore parsing issues
    }
    return ''
  }, [selectedBranchId])

  const carDetailsSegment = useMemo(() => {
    if (selectedTypeId !== 4) {
      return ''
    }
    const color = carColor?.trim()
    const brandOrModel = (carBrand || carModel)?.trim()
    const parts = []
    if (color) {
      parts.push(color)
    }
    if (brandOrModel) {
      parts.push(brandOrModel)
    }
    return parts.join('/')
  }, [carBrand, carColor, carModel, selectedTypeId])

  const orderDetailsSummary = useMemo(() => {
    if (!isCustomerService || !hasCompletedOrderDetails || !selectedOrderType) {
      return ''
    }
    const segments: string[] = []
    const orderTypeLabel = getOrderTypeLabel(selectedOrderType, t)
    if (orderTypeLabel) {
      segments.push(orderTypeLabel)
    }
    if (carDetailsSegment) {
      segments.push(carDetailsSegment)
    }
    if (branchLabel) {
      segments.push(branchLabel)
    }
    return segments.join(' - ')
  }, [
    isCustomerService,
    hasCompletedOrderDetails,
    selectedOrderType,
    carDetailsSegment,
    branchLabel,
    t,
  ])

  // Check if there are items in cart (including CS cart)
  const hasItems = items.length > 0

  // Show options button if there's a customer or items
  const shouldShowOptionsButton = hasCustomerData || hasItems

  const handleAddCustomer = () => {
    dispatch(openModal())
  }

  const handleOpenOptionsMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsOptionsMenuOpen(true)
  }

  const handleCloseOptionsMenu = () => {
    setIsOptionsMenuOpen(false)
  }

  const cleanupDiscountAndTable = async () => {
    try {
      await dispatch(clearSelectedTable() as any)
    } catch (error) {
      // ignore if table removal fails
    } finally {
      dispatch(clearDiscount())
    }
  }

  // Handle clear cart - uses API
  const handleClearCart = async () => {
    if (isCustomerService) {
      const hasCsIdentifier = Boolean(csCartId) || (csCustomerId && csCustomerId > 0)
      if (!hasCsIdentifier) {
        dispatch(clearCsCartLocally())
        await cleanupDiscountAndTable()
        handleCloseOptionsMenu()
        return
      }

      setIsClearingCart(true)
      try {
        let endpoint: string | null = null
        if (csCartId) {
          endpoint = buildEndpoint(`/cashier/carts/${csCartId}`)
        } else if (csCustomerId && csCustomerId > 0) {
          endpoint = buildEndpoint(`/cashier/carts/user/${csCustomerId}`)
        }

        if (endpoint) {
          await apiService.delete(endpoint)
        }
      } catch (error) {
        // Error logged via logger utility
      } finally {
        dispatch(clearCsCartLocally())
        if (csCustomerId && csCustomerId > 0) {
          try {
            await dispatch(
              refreshCsCartItems({ customerId: csCustomerId, forceRefresh: true }) as any
            )
          } catch {
            // ignore refresh errors
          }
        }
        await cleanupDiscountAndTable()
        setIsClearingCart(false)
        handleCloseOptionsMenu()
      }
      return
    }

    if (!cartId) {
      // If no cart ID, just clear locally
      dispatch(clearCartItemsImmediately())
      await cleanupDiscountAndTable()
      handleCloseOptionsMenu()
      return
    }

    setIsClearingCart(true)
    try {
      // Delete cart using API
      await apiService.delete(buildEndpoint(`/cashier/carts/${cartId}`))
    } catch (error) {
      logError('Failed to clear cart', error)
    } finally {
      dispatch(clearCartItemsImmediately())
      await cleanupDiscountAndTable()
      handleCloseOptionsMenu()
      setIsClearingCart(false)
    }
  }

  // Handle reset order - clears customer and cart (server cart too, so it doesn't
  // reappear the next time this customer is selected)
  const handleResetOrder = async () => {
    if (!isCustomerService) {
      await dispatch(clearCartItemsFromApi() as any)
    }
    dispatch(resetOrder())
    dispatch(clearCartItemsImmediately())
    await cleanupDiscountAndTable()
    handleCloseOptionsMenu()
  }

  // Helper function to get current userId (cashier or customer)
  const getCurrentUserId = (): number => {
    try {
      // Try to get from localStorage (cashier ID)
      const employeeDataStr = localStorage.getItem('employee_data')
      if (employeeDataStr) {
        const employeeData = JSON.parse(employeeDataStr)
        if (employeeData.user?.id) {
          return Number(employeeData.user.id)
        }
        if (employeeData.user_id) {
          return Number(employeeData.user_id)
        }
      }
    } catch (e) {
      // Ignore errors
    }
    return 7 // Default fallback
  }

  // Handle delete customer - clears customer, coupon, and table
  const finalizeCustomerRemoval = () => {
    dispatch(clearCustomerData())
    dispatch(clearCartItemsImmediately())
    dispatch(resetCsFlow())
    dispatch(resetCsCart())
    // Clear RTK Query cache to prevent showing old customer data
    dispatch(csFlowApi.util.invalidateTags(['CsFlow', 'CsCart']))
  }

  const handleDeleteCustomer = async () => {
    try {
      const currentUserId = getCurrentUserId()
      const tableId = cartInfo?.table_id ? Number(cartInfo.table_id) : undefined
      let hasRemovedSomething = false

      // Remove coupon if exists
      if (hasDiscount) {
        try {
          await removeCoupon({
            userId: currentUserId,
            tableId,
          }).unwrap()
          
          // Reload cart items to reflect the updated prices
          await dispatch(loadCartItems({ forceRefresh: true, tableId }) as any)
          
          dispatch(clearDiscount())
          hasRemovedSomething = true
        } catch (error: any) {
          // Still clear locally even if API fails
          dispatch(clearDiscount())
          hasRemovedSomething = true
        }
      }

      // Remove table if exists
      if (selectedTable > 0 || tableId) {
        try {
          // Get coupon code if still available (might have been removed above)
          const couponCode = hasDiscount ? undefined : (cartInfo?.discount_code || undefined)
          
          await removeTable({
            userId: currentUserId,
            couponCode,
          }).unwrap()
          
          // Reload cart items to reflect the updated prices
          await dispatch(loadCartItems({ forceRefresh: true }) as any)
          
          await dispatch(clearSelectedTable() as any)
          hasRemovedSomething = true
        } catch (error: any) {
          // Still clear locally even if API fails
          await dispatch(clearSelectedTable() as any)
          hasRemovedSomething = true
        }
      }

      // Transfer cart back to cashier before clearing customer (only for regular cashier flow, not CS)
      if (cartId && hasCustomerData && !isCustomerService) {
        try {
          // Use updateCartUser API to transfer cart back to cashier
          await updateCartUser({
            cartId: Number(cartId),
            userId: currentUserId, // Cashier ID
          }).unwrap()
          
          // Get cart data directly with cashier ID to ensure we get the updated cart
          const cartDataResponse = await getCartData({
            userId: currentUserId,
            couponCode: undefined,
            tableId: tableId,
          }).unwrap()
          
          // Update cart state with the retrieved data
          // Format the data correctly for updateCartDataFromApi
          if (cartDataResponse) {
            const cartDataToUpdate = {
              items: cartDataResponse.items || [],
              ...cartDataResponse
            }
            dispatch(updateCartDataFromApi(cartDataToUpdate))
          }
          
          hasRemovedSomething = true
        } catch (error: any) {
          // Log error but continue with customer removal
          logError('Failed to transfer cart back to cashier', error)
        }
      }

      // Clear customer, cart, and CS flow state
      finalizeCustomerRemoval()

      // Refresh cart items after clearing customer to ensure UI is updated
      // This ensures the cart is properly displayed after transfer back to cashier
      if (!isCustomerService) {
        try {
          await dispatch(loadCartItems({ forceRefresh: true, tableId: tableId }) as any)
        } catch (error: any) {
          // Log error but continue
          logError('Failed to refresh cart after customer removal', error)
        }
      }

      // Show notification if something was removed
      if (hasRemovedSomething) {
        toast.success(
          t('customer.customer_and_data_removed') || 'تم مسح العميل والكوبون والطاولة',
          {
            duration: 4000,
            position: 'top-center',
          }
        )
      }

      handleCloseOptionsMenu()
    } catch (error: any) {
      // Still clear everything even if there's an error
      finalizeCustomerRemoval()
      dispatch(clearDiscount())
      dispatch(clearSelectedTable() as any)
      handleCloseOptionsMenu()
    }
  }


  return (
    <>
      {/* Customer Modal */}
      <CustomerModal
        isOpen={isModalOpen}
        onClose={() => dispatch(closeModal())}
        initialPhone=""
        initialName=""
      />
      
      {/* Options Menu Popup */}
      <Dialog
        open={isOptionsMenuOpen}
        onClose={handleCloseOptionsMenu}
        className="relative z-50"
      >
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

        {/* Modal Container */}
        <div className="fixed inset-0 flex items-center justify-center p-4 pt-20">
          <Dialog.Panel
            className={`
              w-[480px] bg-white rounded-2xl flex flex-col justify-start items-center overflow-hidden shadow-2xl
              ${isRTL ? 'text-right' : 'text-left'}
            `}
          >
            {/* Header */}
            <div className="self-stretch p-4 bg-white inline-flex justify-start items-center gap-6 overflow-hidden">
              <button
                type="button"
                onClick={handleCloseOptionsMenu}
                className="size-9 bg-white rounded-xl   border border-gray-200 flex justify-center items-center hover:bg-gray-50 transition-colors"
              >
                <X className="size-5 text-gray-600" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="self-stretch px-4 pt-2 pb-6 bg-white flex flex-col justify-center items-start gap-3 overflow-hidden">
              {/* Clear Cart */}
              <button
                type="button"
                onClick={handleClearCart}
                disabled={isClearingCart}
                className={`self-stretch h-12 px-4 bg-neutral-100 rounded-xl inline-flex items-center justify-between gap-3 overflow-hidden hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed `}
              >
                {/* Left side - Icon */}
                <div className={`flex items-center gap-3 `}>
                  <img 
                    src={salleIcon}
                    alt="Clear cart"
                    className="w-5 h-5 object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                    }}
                  />
                  <div className={`text-text-primary text-sm font-medium font-['IBM_Plex_Sans_Arabic'] leading-5 `}>
                    {isClearingCart ? t('cart.clearing') : t('cart.clear_cart')}
                  </div>
                </div>
                {/* Right side - Chevron */}
              {isRTL ? <ChevronLeft className={`w-5 h-5 text-black `} /> : <ChevronRight className={`w-5 h-5 text-black `} />}
              </button>

              {/* Reset Order */}
              <button
                type="button" 
                onClick={handleResetOrder}
                className={`self-stretch h-12 px-4 bg-neutral-100 rounded-xl inline-flex items-center justify-between gap-3 overflow-hidden hover:bg-neutral-200 transition-colors`}
              >
                {/* Left side - Icon */}
                <div className={`flex items-center gap-3`}>
                  <img 
                    src={refreshIcon}
                    alt="Reset order"
                    className="w-5 h-5 object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                    }}
                  />
                  <div className={`text-text-primary text-sm font-medium font-['IBM_Plex_Sans_Arabic'] leading-5 line-clamp-1`}>
                    {t('order.reset_order')}
                  </div>
                </div>
            {/* Right side - Chevron */}
            {isRTL ? <ChevronLeft className={`w-5 h-5 text-black `} /> : <ChevronRight className={`w-5 h-5 text-black `} />}
              </button>

              {/* Delete Customer */}
              {hasCustomerData && (
                <button
                  type="button"
                  onClick={handleDeleteCustomer}
                  className={`self-stretch h-12 px-4 bg-neutral-100 rounded-xl inline-flex items-center justify-between gap-3 overflow-hidden hover:bg-neutral-200 transition-colors`}
                >
                  {/* Left side - Icon */}
                  <div className={`flex items-center gap-3`}>
                    <img 
                      src={deleteUserIcon}
                      alt="Delete customer"
                      className="w-5 h-5 object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                    <div className={`text-text-primary text-sm font-medium font-['IBM_Plex_Sans_Arabic'] leading-5 line-clamp-1`}>
                      {t('customer.clear_customer')}
                    </div>
                  </div>
                {/* Right side - Chevron */}
                {isRTL ? <ChevronLeft className={`w-5 h-5 text-black `} /> : <ChevronRight className={`w-5 h-5 text-black `} />}
                </button>
              )}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <div className="mb-1">
        <div className={`
          h-16 bg-white rounded-2xl px-4 py-2
          flex items-start ${shouldShowOptionsButton ? 'justify-between' : 'justify-end'} flex-row-reverse
        `}>
          {/* Left Side - Options Button */}
          {shouldShowOptionsButton && (
            <button
              type="button"
              onClick={handleOpenOptionsMenu}
              onMouseDown={(e) => e.stopPropagation()}
              className="w-8 h-8 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-gray-200 transition-colors duration-200 relative z-10"
              aria-label="Open options menu"
            >
              <MoreVertical className="h-4 w-4 text-gray-600" />
            </button>
          )}

          {/* Customer Info or Add Button - Right Side */}
          <div className={`flex items-start justify-start gap-1 flex-row-reverse`}>
            {!hasCustomerData ? (
              // Add Customer Button (equivalent to Flutter's empty state)
              <div className={`flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-xl flex-row-reverse`}>
                <span className={`text-sm font-medium text-flutter-black font-['IBMPlexSansArabic'] `}>
                  {t('customer.add_customer')}
                </span>
                <button
                  onClick={handleAddCustomer}
                  className="w-9 h-9 bg-white rounded-xl flex items-center justify-center  transition-colors duration-200"
                >
                  <Plus className="h-4 w-4 text-black" />
                </button>
              </div>
            ) : (
              // Customer Info Display (equivalent to Flutter's customer info)
              <div className="flex items-center gap-3">
                <button
                  onClick={handleAddCustomer}
                  className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors duration-200"
                >
                  <img src={usersIcon} alt="user" className="w-4 h-4" />
                </button>
                <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
                  <p className="text-sm font-medium text-flutter-black font-['IBMPlexSansArabic']">
                    {customerName || '---'}
                  </p>
                  <p className="text-xs text-gray-500 font-['IBMPlexSansArabic']">
                    {customerPhone || '---'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Select Order Details Button - for CS users only, separate card */}
        {/* Show button when customer number is validated in CS flow - always visible for CS users */}
        {isCustomerService && onSelectOrderDetails && (isCustomerNumberValid || hasCustomerData) && (
          <button
            onClick={onSelectOrderDetails}
            className={`
              w-full h-14 bg-white rounded-xl px-4 mt-2
              flex items-center justify-between gap-2
              hover:bg-gray-50 transition-colors
               'flex-row-reverse'
            `}
          >
            <div className={`flex items-center gap-2 flex-row-reverse`}>
       
              <span className="text-sm font-medium text-flutter-black font-['IBM_Plex_Sans_Arabic']">
                {orderDetailsSummary || t('cs.select_order_details') || 'اختر تفاصيل الطلب'}
              </span>
              <img 
                src={carOrdersIcon} 
                alt="car orders" 
                className="w-5 h-5"
              />
            </div>
         {isRTL ? <ChevronLeft className={`w-5 h-5 text-[#525866] `} /> : <ChevronRight className={`w-5 h-5 text-[#525866] `} />}
          </button>
        )}
      </div>
    </>
  )
}

export default CustomerInfoHeader
